// Pure validation functions for agent-skills frontmatter.
// Used by the editor form (inline errors) and the preview pane (Validation section).
// Editor spec §8 defines all rules; this module implements them.

import type { Frontmatter, AgentFileKind } from "@/types/agent-skills";

export interface ValidationIssue {
  code: string;
  message: string;
}

export interface ValidationResult {
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

/**
 * Validate a frontmatter object against editor spec §8 rules.
 *
 * @param frontmatter  The parsed frontmatter to validate.
 * @param kind         The AgentFile kind ('agent' | 'skill' | 'subskill').
 * @param siblings     Other non-deleted sibling files under the same parent
 *                     (used for duplicate-name detection). Pass [] if unknown.
 */
export function validateFrontmatter(
  frontmatter: Frontmatter,
  kind: AgentFileKind,
  siblings: { name: string }[] = []
): ValidationResult {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];

  // ── Error: name missing or not slug ─────────────────────────────────────
  if (!frontmatter.name || frontmatter.name.trim() === "") {
    errors.push({ code: "name_required", message: "name is required." });
  } else if (!SLUG_PATTERN.test(frontmatter.name)) {
    errors.push({
      code: "name_invalid_slug",
      message:
        "name must match ^[a-z0-9][a-z0-9-]*$ (lowercase letters, digits, hyphens; no leading hyphen).",
    });
  }

  // ── Error: description missing or > 500 chars ────────────────────────────
  if (!frontmatter.description || frontmatter.description.trim() === "") {
    errors.push({ code: "description_required", message: "description is required." });
  } else if (frontmatter.description.length > 500) {
    errors.push({
      code: "description_too_long",
      message: `description must be 500 characters or fewer (currently ${frontmatter.description.length}).`,
    });
  }

  // ── Error: trigger_type required for skills/subskills ────────────────────
  if (kind === "skill" || kind === "subskill") {
    if (!frontmatter.trigger_type) {
      errors.push({
        code: "trigger_type_required",
        message: "trigger_type is required for skills and sub-skills.",
      });
    } else if (frontmatter.trigger_type !== "description" && frontmatter.trigger_type !== "rule") {
      errors.push({
        code: "trigger_type_invalid",
        message: "trigger_type must be 'description' or 'rule'.",
      });
    }
  }

  // ── Error: trigger_rule required + well-formed when trigger_type === 'rule' ─
  if (frontmatter.trigger_type === "rule") {
    const rule = frontmatter.trigger_rule;
    if (!rule) {
      errors.push({
        code: "trigger_rule_required",
        message: "trigger_rule is required when trigger_type is 'rule'.",
      });
    } else {
      const validOps = ["equals", "contains", "gt", "lt", "regex"];
      const missingFields: string[] = [];
      if (!rule.field || String(rule.field).trim() === "") missingFields.push("field");
      if (!rule.op || !validOps.includes(rule.op)) {
        errors.push({
          code: "trigger_rule_invalid_op",
          message: `trigger_rule.op must be one of: ${validOps.join(", ")}.`,
        });
      }
      // value can be 0 (numeric) so check for undefined/null explicitly
      if (rule.value === undefined || rule.value === null || String(rule.value).trim() === "") {
        missingFields.push("value");
      }
      if (missingFields.length > 0) {
        errors.push({
          code: "trigger_rule_malformed",
          message: `trigger_rule is missing required fields: ${missingFields.join(", ")}.`,
        });
      }
    }
  }

  // ── Error: duplicate name among siblings ─────────────────────────────────
  const currentName = frontmatter.name?.trim();
  if (currentName) {
    const duplicate = siblings.some((s) => s.name === currentName);
    if (duplicate) {
      errors.push({
        code: "name_duplicate",
        message: `A sibling named "${currentName}" already exists.`,
      });
    }
  }

  // ── Warning: body is empty ────────────────────────────────────────────────
  // NOTE: body is not part of frontmatter; callers pass body as a sibling.
  // This rule is checked via validateBody() below, but we expose a flag here
  // for completeness — the PreviewPane passes body separately.

  // ── Warning: description trigger with short description ──────────────────
  if (
    (kind === "skill" || kind === "subskill") &&
    frontmatter.trigger_type === "description" &&
    frontmatter.description &&
    frontmatter.description.trim().length < 20
  ) {
    warnings.push({
      code: "description_trigger_too_vague",
      message:
        "Description is very short (< 20 chars) — consider expanding it for clearer routing.",
    });
  }

  return { errors, warnings };
}

/**
 * Separate validator for the body field (returns a warning if empty).
 * Kept separate because PreviewPane passes body independently from frontmatter.
 */
export function validateBody(body: string): ValidationIssue[] {
  if (!body || body.trim() === "") {
    return [{ code: "body_empty", message: "Body is empty." }];
  }
  return [];
}
